// GolfLeagueTrackerAlternative.js
// Comparison: Golf League Tracker vs Tee Time Organizer.
// Framing: deep league specialist vs broad organizer platform. Concede GLT's
// league-reporting depth; locate TTO's edge on group-type breadth, tee-time
// scheduling (single / recurring / bulk), and the free-plus-monthly model.
//
// ACCURACY: GLT claims sourced from their published site and dated. TTO league
// standings is NOT claimed as live — single-date scoring is live, standings is
// on the roadmap, so the standings row reads "Coming soon." Games read as
// "expanding." Re-verify GLT pricing/features before each quarterly review.

import React from 'react';
import {
  H2,
  P,
  Callout,
  NoteBox,
  ComparisonTable,
  CompareCTA,
  FAQ,
  AttributionFooter,
} from '../compareUI';
import { colors } from '../compareData';

const REVIEWED = 'June 2026';

const FAQS = [
  {
    q: 'Can Tee Time Organizer run a competitive league?',
    a: 'It handles league play with scoring and handicaps, and season-long standings are on the near-term roadmap. Golf League Tracker currently goes deeper on league-specific reporting and settings; Tee Time Organizer covers a wider range of group types around league play.',
  },
  {
    q: 'I run a buddies group, MGA, or LGA — not a formal league. Which fits?',
    a: 'Tee Time Organizer. It is built for the organizer running recurring or casual groups — scheduling, RSVPs, waitlists, pairings, GHIN handicaps, and scoring — without forcing everything into a league-season structure.',
  },
  {
    q: 'Does Golf League Tracker do tee time scheduling and RSVPs?',
    a: 'Its scheduling is built around generating a league season, not coordinating individual tee times with RSVPs and a waitlist. If day-to-day round coordination is your main need, that is Tee Time Organizer\u2019s focus.',
  },
  {
    q: 'Is there a free way to try it?',
    a: 'Tee Time Organizer has a permanent free plan for up to 8 players. Golf League Tracker offers a limited trial, then per-league, per-season pricing.',
  },
];

export default function GolfLeagueTrackerAlternative() {
  return (
    <article>
      <p
        style={{
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontSize: 12,
          fontWeight: 700,
          color: colors.green700,
          margin: '0 0 14px',
        }}
      >
        Golf group software · Comparison
      </p>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 34,
          fontWeight: 400,
          color: colors.gray900,
          lineHeight: 1.18,
          margin: '0 0 16px',
        }}
      >
        Golf League Tracker alternative: how Tee Time Organizer compares
      </h1>
      <p style={{ fontSize: 19, color: colors.gray600, lineHeight: 1.5, margin: '0 0 14px' }}>
        Golf League Tracker is a deep, league-first tool. Tee Time Organizer is a broader platform
        for buddies groups, associations, and casual play — and it runs leagues too. Here's how to
        tell which fits your group.
      </p>
      <p
        style={{
          fontSize: 13,
          color: colors.gray500,
          borderBottom: `1px solid ${colors.gray200}`,
          paddingBottom: 22,
          margin: '0 0 30px',
        }}
      >
        Last reviewed {REVIEWED} · Comparison based on each product's publicly available information
      </p>

      <P>
        If you're choosing between Golf League Tracker and Tee Time Organizer, the honest answer
        depends less on which has "more features" and more on what kind of golf group you run. These
        two products start from different places: one begins with the competitive league and builds
        outward; the other begins with the organizer and the round, and serves leagues as one of
        several things it does. Both can run a league. They're not built for the same organizer.
      </P>

      <NoteBox label="The one question that decides it">
        <strong>What kinds of groups do you run?</strong>
        <br />
        <br />
        If your answer is a single, serious competitive league that lives on its standings and weekly
        reports — Golf League Tracker's specialization is a real strength, and it goes deeper on
        league statistics than we do today.
        <br />
        <br />
        If your answer is a buddies group, a men's or women's association, a weekend game, a league,
        or several of these at once — that breadth is where Tee Time Organizer fits and a league-only
        tool doesn't.
      </NoteBox>

      <H2>Where Golf League Tracker is genuinely strong</H2>
      <P>
        Credit where it's earned: Golf League Tracker is a mature, league-first product, and its
        depth in that lane is real. It offers an enormous range of configurable league settings,
        detailed season standings and points, hole-by-hole scoring history, year-over-year
        statistics, automated sub-requests, and print-ready, professional reports. For a long-running
        competitive league whose members care deeply about standings and weekly results, that depth
        is a legitimate reason to choose it.
      </P>

      <H2>Where Tee Time Organizer fits</H2>
      <P>
        Most golf organizers aren't running just one formal competitive league. They're running a
        Saturday buddies group, or a men's golf association, or a ladies' association, or a casual
        weekly game, or a league — and often more than one of these at the same time. Those groups
        need tee time scheduling, one-click RSVPs, an auto-promoting waitlist, pairings, GHIN
        handicaps, and scoring, without being pushed into a league-season framework when the group
        isn't structured that way.
      </P>
      <P>
        That's the gap Tee Time Organizer fills. It's built for the organizer and the round first, so
        it serves buddies groups, MGAs, and LGAs natively — and it runs league play too, with
        season-long standings on the near-term roadmap.
      </P>

      <H2>A note on scheduling, because it's easy to confuse</H2>
      <P>
        Both products have a "scheduling" feature, but they mean different things by it. Golf League
        Tracker's scheduling builds a league season — generate a multi-week schedule with position
        rounds and matchups. Tee Time Organizer's scheduling is tee time coordination, and it flexes
        across the ways a group actually plays: a single tee time for this weekend's round, recurring
        tee times for ongoing league play, or bulk tee times across one day for a tournament or
        outing. RSVPs and an auto-promoting waitlist run on top of all three. So if your need is
        "build my 16-week league schedule," Golf League Tracker is designed for that; if your need is
        "organize this weekend's round," "set up our weekly league slot," or "lay out the tee sheet
        for our outing," that range is Tee Time Organizer's focus.
      </P>

      <H2>Feature comparison</H2>
      <ComparisonTable
        caption={`Based on the published Golf League Tracker website as of ${REVIEWED}. Golf League Tracker is an active product and may add features over time — we re-check this page quarterly.`}
        columns={['What you need', 'Golf League Tracker', 'Tee Time Organizer']}
        rows={[
          ['Depth of league standings & reports', 'Deeper (league specialist)', 'Coming soon'],
          ['Configurable league/points settings', 'Extensive', 'Roadmap'],
          ['Season schedule builder', 'Yes', 'Recurring tee times'],
          ['Sub-request automation', 'Yes', 'On roadmap'],
          ['Single-round mobile scoring', 'Yes', 'Yes'],
          ['Tee time scheduling & RSVPs', 'Not the focus', 'Yes'],
          ['Auto-promoting waitlist', 'Not listed', 'Yes'],
          ['Bulk tee times (tournaments/outings)', 'Not listed', 'Yes'],
          ['Fits buddies groups / MGA / LGA', 'League-oriented', 'Yes (built for it)'],
          ['GHIN handicap sync', 'Handicap calc', 'Yes (automatic sync)'],
          ['Weather on each tee time', 'Not listed', 'Yes'],
          ['SMS + email reminders', 'Email', 'SMS + email'],
          ['Free plan', 'Trial only', 'Yes (up to 8 players)'],
        ]}
      />
      <p style={{ fontSize: 14, color: colors.gray500, lineHeight: 1.6, margin: '0 0 18px' }}>
        "Not listed" / "Not the focus" means the capability isn't a published emphasis of that
        product as of the review date — not that it's impossible. We describe differences in focus
        rather than guessing at what either tool does internally.
      </p>

      <H2>Pricing works differently, too</H2>
      <P>
        The pricing models reflect the different designs. Golf League Tracker is priced per league,
        per season — its published rate is $119 per league for a season of up to 30 rounds, with a
        limited trial rather than a free tier, as listed on its site as of {REVIEWED}. (Confirm
        current pricing on their site.)
      </P>
      <P>
        Tee Time Organizer is a monthly subscription with a permanent free plan (up to 8 players),
        scaling by group size: Starter $4.99/month, Plus $9.99/month, Professional $14.99/month, and
        League $24.99/month for full season management. The practical difference: if you run a single
        season-long competitive league, per-season pricing is straightforward. If you run a buddies
        group or association that plays year-round, or you manage more than one group, a free-to-start
        monthly model usually fits better than paying per league, per season.
      </P>

      <CompareCTA
        heading="See which fits your group"
        body="Start free in about 30 seconds — no credit card, no time limit. Run a buddies group, an association, or a league, and only upgrade when you need to."
      />

      <FAQ id="golf-league-tracker" faqs={FAQS} />

      <AttributionFooter competitor="Golf League Tracker" reviewed={REVIEWED} />
    </article>
  );
}
